import { Bell, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  dismissPushPrompt,
  getPushPermission,
  isPushPromptDismissed,
  registerPushSubscription,
} from '../pwa';

/**
 * In-line "Get updates when there's news about your pet's stay?" banner.
 *
 * Renders only when:
 *   - Browser supports Notification + PushManager
 *   - Current permission is 'default' (never asked or reset)
 *   - The customer hasn't previously dismissed
 *
 * Asks via the gentle in-app banner first; only after the customer clicks
 * "Yes, notify me" do we fire the browser's permission prompt — this avoids
 * the universal anti-pattern of asking on first load.
 */
export function PushPermissionPrompt({ context }: { context?: 'home' | 'booking' }) {
  const [perm, setPerm] = useState<ReturnType<typeof getPushPermission>>(() => getPushPermission());
  const [hidden, setHidden] = useState<boolean>(() => isPushPromptDismissed());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPerm(getPushPermission());
  }, []);

  if (hidden) return null;
  if (perm === 'unsupported' || perm === 'granted' || perm === 'denied') return null;

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await registerPushSubscription();
      if (res.ok) {
        setPerm('granted');
        setHidden(true);
      } else if (res.reason === 'denied') {
        setError('Notifications were blocked in your browser settings.');
        setPerm('denied');
      } else if (res.reason === 'unsupported') {
        setError('Your browser does not support push notifications.');
        setPerm('unsupported');
      } else {
        setError('Could not enable notifications. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    dismissPushPrompt();
    setHidden(true);
  };

  const message =
    context === 'booking'
      ? "Get updates while your pet's with us — pickup-time reminders, check-in confirmations, photo updates."
      : "Get updates when there's news about your pet's stay — pickup reminders, check-ins, photo updates.";

  return (
    <section
      aria-label="Enable push notifications"
      style={{
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
        borderRadius: '14px',
        background: 'var(--surface, white)',
        padding: '1rem 1.1rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.85rem',
        marginBottom: '1rem',
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: '9px',
          background: 'oklch(0.96 0.02 80)',
          color: 'var(--brand-primary, var(--primary, #004AAD))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Bell size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.92rem', fontWeight: 600, marginBottom: '0.2rem' }}>
          Stay in the loop
        </div>
        <p
          style={{
            margin: 0,
            fontSize: '0.84rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            lineHeight: 1.45,
          }}
        >
          {message}
        </p>
        {error && (
          <p
            style={{
              margin: '0.4rem 0 0',
              fontSize: '0.78rem',
              color: 'var(--destructive, oklch(0.55 0.15 30))',
            }}
          >
            {error}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem' }}>
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            style={{
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--brand-primary, var(--primary, #004AAD))',
              color: 'white',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Enabling…' : 'Yes, notify me'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            style={{
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'transparent',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
              fontSize: '0.82rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Not now
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss notifications prompt"
        style={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          border: 'none',
          background: 'transparent',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          cursor: 'pointer',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <X size={14} />
      </button>
    </section>
  );
}
